import React, { useEffect, useMemo, useState } from 'react';
import { ContentFrame, LayoutContent, LinearProgress, Typography, ScreenContainer, } from '@pinwheel/origami';
import { useSelector } from 'react-redux';
import ModalHeader from '../components/ModalHeader';
import Loading from '../LinkApiRouter/components/Loading';
import { selectIsLoginDefaultStep } from '../state/slices/slice-link-api';
import { LoadingEllipsisText } from '../components/LoadingEllipsisText';
import { useGetLocalizedString } from '../hooks/useGetLocalizedString';
// A fake progress bar percentage that approaches 100% (but never goes to 100%
// completely) after around ~15 seconds.
var useProgressPercent = function (_a) {
    var _b = _a === void 0 ? {} : _a, 
    // Note: starting at a non-zero number gives some semblance of progress
    _c = _b.startingPercent, 
    // Note: starting at a non-zero number gives some semblance of progress
    startingPercent = _c === void 0 ? 5 : _c, _d = _b.updateFrequencyMs, updateFrequencyMs = _d === void 0 ? 1500 : _d;
    var _e = useState(startingPercent), progressBarPercent = _e[0], setProgressBarPercent = _e[1];
    useEffect(function () {
        // A very rudimentary progress implementation which tries to go to the
        // halfway point between the current progress and 100%, but never goes
        // beyond 10% per interval.
        // TODO: come up with a better progress algorithm here and make hardcoded
        //  values configurable
        var interval = setInterval(function () {
            setProgressBarPercent(function (prevPercent) {
                return Math.min((prevPercent + 100) / 2, prevPercent + 10);
            });
            // TODO: we may want to add a jitter here to make it less obvious that
            //  we're faking the progress.
        }, updateFrequencyMs);
        return function () { return clearInterval(interval); };
    }, [updateFrequencyMs]);
    return progressBarPercent;
};
var useDynamicLoadingText = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.updateFrequencyMs, updateFrequencyMs = _c === void 0 ? 4000 : _c;
    var isFirstLoginStep = useSelector(selectIsLoginDefaultStep);
    var getLocalizedString = useGetLocalizedString();
    var textOptions = useMemo(function () {
        return isFirstLoginStep
            ? [
                getLocalizedString('SETTING_UP_SECURE_CONNECTION'),
                getLocalizedString('CONNECTING_YOUR_ACCOUNT'),
                getLocalizedString('ALMOST_THERE_MAY_TAKE_FEW_MOMENTS'),
            ]
            : [
                getLocalizedString('WORKING_ON_GETTING_YOU_CONNECTED'),
                getLocalizedString('ALMOST_THERE_MAY_TAKE_FEW_MOMENTS'),
            ];
    }, [getLocalizedString, isFirstLoginStep]);
    var _d = useState(0), textOptionIndex = _d[0], setTextOptionIndex = _d[1];
    useEffect(function () {
        var interval = setInterval(function () {
            setTextOptionIndex(function (prevIndex) {
                return prevIndex === textOptions.length - 1 ? prevIndex : prevIndex + 1;
            });
        }, updateFrequencyMs);
        return function () { return clearInterval(interval); };
    }, [textOptions, updateFrequencyMs]);
    return textOptions[textOptionIndex] || '';
};
// A more "dynamic" version of the <Loading> view that attempts to reduce user
// drop-offs during long login load times. This is currently optimized to retain
// users until at least the ~15-20s wait time mark, which is where we see the
// highest rate of increase in drop-off percentage:
// https://www.notion.so/pinwheelapi/Link-Login-Loading-Time-Analysis-d44ef14c5f734d4a87cfe3fbfa913db8
var LoginLoading = function (_a) {
    var hideModalHeader = _a.hideModalHeader, progressBarUpdateFrequencyMs = _a.progressBarUpdateFrequencyMs, loadingTextUpdateFrequencyMs = _a.loadingTextUpdateFrequencyMs, ellipsisUpdateFrequencyMs = _a.ellipsisUpdateFrequencyMs;
    var progressBarPercent = useProgressPercent({
        updateFrequencyMs: progressBarUpdateFrequencyMs,
    });
    var loadingText = useDynamicLoadingText({
        updateFrequencyMs: loadingTextUpdateFrequencyMs,
    });
    return (React.createElement(ScreenContainer, { "data-testid": "LoginLoadingView" },
        hideModalHeader ? null : React.createElement(ModalHeader, null),
        React.createElement(ContentFrame, null,
            React.createElement(LayoutContent, null,
                React.createElement("div", { style: {
                        width: '100%',
                        // Makes the content be closer to the center of the entire screen,
                        // since having 100% height would cause it to be slightly offset due
                        // to the modal header's height.
                        height: '80%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }, "aria-busy": true },
                    React.createElement("div", { 
                        // Wrapper with a fixed height since <Loading> has `height: 100%`
                        style: {
                            width: '100%',
                            height: '100px',
                        } },
                        React.createElement(Loading, null)),
                    React.createElement(LinearProgress, { variant: "determinate", progress: progressBarPercent }),
                    React.createElement("div", { style: { marginTop: '30px' } },
                        React.createElement(Typography, { align: "center", variant: "body", color: "light" },
                            loadingText,
                            React.createElement(LoadingEllipsisText, { updateFrequencyMs: ellipsisUpdateFrequencyMs }))))))));
};
export default LoginLoading;
